import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Calendar, Check, ClipboardCopy, Undo2, ExternalLink, MapPin, Minus, Plus, UserPlus, Users, X } from 'lucide-react';
import { jobSiteLine } from '../../lib/job-site.js';
import {
  BLOCK_HOURS,
  durationLabel,
  hourLabel,
  readAssignment,
  readCompletedAt,
  readHelpers,
  shiftIsoDate,
  todayLocalIso,
  useDispatchBoard,
  type Technician,
} from '../../lib/dispatch-board.js';
import { tradeLabel, type Trade } from '../../lib/trade.js';
import { JobVisits } from '../job-visits.js';
import { api, ApiError, type FieldPhase, type OfficePhaseResult, type SalesOrderSummary, type SalesType } from '../../lib/api.js';
import { phaseLabel } from '../../lib/field-time.js';
import { FOP_UDF } from '../../lib/dispatch-board.js';

interface DetailPanelProps {
  salesTypes: ReadonlyArray<SalesType>;
  techs: ReadonlyArray<Technician>;
  /** Off-roster (deactivated) techs still holding a job on this day — named, but never offered as a reassign target. */
  offRosterTechs?: ReadonlyArray<Technician>;
  placed: ReadonlyArray<{
    order: SalesOrderSummary;
    trade: Trade;
    techId: string;
    date: string;
    startHour: number;
    duration: number;
  }>;
  /** Extra visits (lib/visits.ts) on the selected day — lets a click on a
   * visit block open this panel when the order's own schedule is another day. */
  visitsToday: ReadonlyArray<{
    order: SalesOrderSummary;
    techId: string;
    date: string;
    startHour: number;
    duration: number;
  }>;
  /** Triggers a Spire UDF PUT to clear the assignment. */
  onUnassign: (orderId: number) => void;
  /**
   * Resize-in-place: dispatcher tweaks startHour / duration after the drop.
   * Same write path as the initial assign — PUTs the FOP_* UDFs.
   */
  onResize: (orderId: number, startHour: number, duration: number) => void;
  /** Cross-day move — preserves tech / start / duration, just updates FOP_DATE. */
  onMoveDate: (orderId: number, nextDate: string) => void;
  /** Replace the helpers list (FOP_HELPERS) for the selected order. */
  onSetHelpers: (orderId: number, helperIds: ReadonlyArray<string>) => void;
  /**
   * Reassign the lead technician without a drag — same write path as a
   * board move (preserves date/start/duration). Keyboard + tablet
   * dispatchers previously had NO way to swap techs (audit quick-win).
   */
  onReassignTech: (orderId: number, techId: string) => void;
}

/**
 * The strip beneath the board. Reads the currently-selected placed job
 * out of the dispatch store and offers Unschedule + Message-tech +
 * Open-order actions. Matches `.ib-detail` in the mockup.
 *
 * Plus: start-hour + duration steppers so the dispatcher can resize an
 * assignment without re-dragging. Each change PUTs the FOP_START /
 * FOP_DURATION UDFs.
 */
export function DetailPanel({
  salesTypes,
  techs,
  offRosterTechs = [],
  placed,
  visitsToday,
  onUnassign,
  onResize,
  onMoveDate,
  onSetHelpers,
  onReassignTech,
}: DetailPanelProps) {
  const selectedOrderId = useDispatchBoard((s) => s.selectedOrderId);
  const setSelectedDate = useDispatchBoard((s) => s.setSelectedDate);
  const pushActivity = useDispatchBoard((s) => s.pushActivity);

  const sel = placed.find((p) => p.order.spireId === selectedOrderId);
  const [copyState, setCopyState] = useState<'idle' | 'copied'>('idle');

  // Clicked an extra-visit block whose order is scheduled (first visit) on
  // a different day: no steppers for the first visit here, just the job and
  // its visit list.
  const visitSel = sel ? undefined : visitsToday.find((v) => v.order.spireId === selectedOrderId);
  if (visitSel) {
    const first = readAssignment(visitSel.order.udf ?? {});
    const name =
      techs.find((t) => t.id === visitSel.techId)?.name ??
      offRosterTechs.find((t) => t.id === visitSel.techId)?.name ??
      'unknown';
    return (
      <div
        className="border-t px-4 py-3 space-y-2"
        style={{
          borderColor: 'var(--color-border-tertiary)',
          backgroundColor: 'var(--color-background-secondary)',
        }}
      >
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-medium text-sm text-ink">{visitSel.order.customer.name}</span>
              <span className="text-xs text-ink-muted">— {visitSel.order.orderNo}</span>
              <span
                className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium"
                style={{ backgroundColor: 'var(--pill-neutral-bg)', color: 'var(--pill-neutral-fg)' }}
              >
                Extra visit
              </span>
            </div>
            {jobSiteLine(visitSel.order) && (
              <div className="mt-0.5 text-[11px] text-ink-muted flex items-center gap-1">
                <MapPin className="h-3 w-3 shrink-0" aria-hidden />
                <span>{jobSiteLine(visitSel.order)}</span>
              </div>
            )}
            <div className="mt-0.5 text-[11px] text-ink-muted">
              <span className="font-medium text-ink">{detailDate(visitSel.date)}</span>
              {' · '}
              <span className="font-medium text-ink">{name}</span>{' '}
              · {hourLabel(visitSel.startHour)}–{hourLabel(visitSel.startHour + visitSel.duration)} ({durationLabel(visitSel.duration)})
            </div>
          </div>
          <Link
            to="/sales-orders/$id"
            params={{ id: String(visitSel.order.spireId) }}
            className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs text-ink-muted hover:bg-surface hover:text-ink"
          >
            <ExternalLink className="h-3.5 w-3.5" aria-hidden />
            Open
          </Link>
        </div>
        <JobVisits spireId={visitSel.order.spireId} techs={techs} first={first} onGoToDate={setSelectedDate} />
      </div>
    );
  }

  if (!sel) {
    return (
      <div
        className="grid grid-cols-[1fr_auto] items-center gap-3 border-t px-4 py-3 min-h-[60px]"
        style={{
          borderColor: 'var(--color-border-tertiary)',
          backgroundColor: 'var(--color-background-secondary)',
        }}
      >
        <div className="text-xs text-ink-muted">
          Drag a card onto the timeline to schedule it, or click any placed job to see detail and actions here.
        </div>
        <div />
      </div>
    );
  }

  const offRosterLead = techs.some((t) => t.id === sel.techId)
    ? null
    : (offRosterTechs.find((t) => t.id === sel.techId) ?? null);
  const techName = techs.find((t) => t.id === sel.techId)?.name ?? offRosterLead?.name ?? 'unknown';
  const earlierStart = (next: number) => onResize(sel.order.spireId, next, sel.duration);
  const newDuration = (next: number) => onResize(sel.order.spireId, sel.startHour, next);

  // Steppers move in BLOCK_HOURS (30 min). Bounds are absolute clock hours:
  // 4 AM .. midnight-plus-two covers early starts and late finishes.
  const canShiftEarlier = sel.startHour - BLOCK_HOURS >= 4;
  const canShiftLater = sel.startHour + sel.duration + BLOCK_HOURS <= 26;
  const canShrink = sel.duration - BLOCK_HOURS >= BLOCK_HOURS;
  const canGrow = sel.duration + BLOCK_HOURS <= 12;

  const moveDate = (nextDate: string) => {
    onMoveDate(sel.order.spireId, nextDate);
    // Follow the card to its new day so the dispatcher can verify the
    // landing position without hunting through the date picker.
    setSelectedDate(nextDate);
  };

  const copyAssignmentSummary = async () => {
    // One-line plain-text summary the dispatcher can paste into Slack,
    // SMS, or an email. Doesn't require us to wire a notification
    // transport (Phase 5 — Twilio / Slack webhook). Defensive: navigator
    // .clipboard may be missing in older browsers or non-HTTPS contexts.
    const summary = `${sel.order.orderNo} · ${sel.order.customer.name} · ${techName} · ${detailDate(sel.date)} ${hourLabel(sel.startHour)}–${hourLabel(sel.startHour + sel.duration)}`;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(summary);
      } else {
        // Fallback for older Safari iOS — synthesize a hidden textarea +
        // execCommand. Works without HTTPS for the local dev server.
        const ta = document.createElement('textarea');
        ta.value = summary;
        ta.setAttribute('readonly', '');
        ta.style.position = 'absolute';
        ta.style.left = '-9999px';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      setCopyState('copied');
      pushActivity({
        tone: 'info',
        text: `Copied assignment for ${sel.order.orderNo} — paste anywhere`,
      });
      window.setTimeout(() => setCopyState('idle'), 1800);
    } catch {
      pushActivity({
        tone: 'warning',
        text: `Couldn't copy ${sel.order.orderNo} summary to clipboard`,
      });
    }
  };

  return (
    <div
      className="border-t px-4 py-3 space-y-2"
      style={{
        borderColor: 'var(--color-border-tertiary)',
        backgroundColor: 'var(--color-background-secondary)',
      }}
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            {sel.order.customer.spireId !== null ? (
              <Link
                to="/customers/$id"
                params={{ id: String(sel.order.customer.spireId) }}
                className="font-medium text-sm text-ink hover:underline"
                title="Open customer"
              >
                {sel.order.customer.name}
              </Link>
            ) : (
              <span className="font-medium text-sm text-ink">{sel.order.customer.name}</span>
            )}
            <span className="text-xs text-ink-muted">— {sel.order.orderNo}</span>
            {sel.order.phaseId && (
              <span
                className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-mono"
                style={{ backgroundColor: 'var(--pill-neutral-bg)', color: 'var(--pill-neutral-fg)' }}
                title="Spire order phase"
              >
                {sel.order.phaseId}
              </span>
            )}
            {(() => {
              const completed = readCompletedAt(sel.order.udf ?? {});
              if (!completed) return null;
              return (
                <span
                  className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium"
                  style={{
                    backgroundColor: 'var(--feedback-success-bg)',
                    color: 'var(--feedback-success-fg)',
                  }}
                >
                  <Check className="h-3 w-3" aria-hidden />
                  Done at {completed.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}
                </span>
              );
            })()}
          </div>
          {jobSiteLine(sel.order) && (
            <div className="mt-0.5 text-[11px] text-ink-muted flex items-center gap-1">
              <MapPin className="h-3 w-3 shrink-0" aria-hidden />
              <span>{jobSiteLine(sel.order)}</span>
            </div>
          )}
          <div className="mt-0.5 text-[11px] text-ink-muted">
            <span className="font-medium text-ink">{detailDate(sel.date)}</span>
            {' · '}
            Assigned to <span className="font-medium text-ink">{techName}</span>{' '}
            · {hourLabel(sel.startHour)}–{hourLabel(sel.startHour + sel.duration)} ({durationLabel(sel.duration)})
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          <Link
            to="/sales-orders/$id"
            params={{ id: String(sel.order.spireId) }}
            className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs text-ink-muted hover:bg-surface hover:text-ink"
          >
            <ExternalLink className="h-3.5 w-3.5" aria-hidden />
            Open
          </Link>
          <button
            type="button"
            onClick={() => void copyAssignmentSummary()}
            className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs text-ink-muted hover:bg-surface hover:text-ink"
            title="Copy a one-line assignment summary (paste into Slack / SMS / email)"
          >
            {copyState === 'copied' ? (
              <>
                <Check className="h-3.5 w-3.5" aria-hidden />
                Copied
              </>
            ) : (
              <>
                <ClipboardCopy className="h-3.5 w-3.5" aria-hidden />
                Copy
              </>
            )}
          </button>
          <button
            type="button"
            onClick={() => onUnassign(sel.order.spireId)}
            className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs text-ink-muted hover:bg-surface hover:text-ink"
          >
            <Undo2 className="h-3.5 w-3.5" aria-hidden />
            Unschedule
          </button>
        </div>
      </div>

      <div className="flex items-center gap-3 text-xs flex-wrap">
        <Stepper
          label="Start"
          value={hourLabel(sel.startHour)}
          onDecrement={canShiftEarlier ? () => earlierStart(sel.startHour - BLOCK_HOURS) : undefined}
          onIncrement={canShiftLater ? () => earlierStart(sel.startHour + BLOCK_HOURS) : undefined}
        />
        <Stepper
          label="Duration"
          value={durationLabel(sel.duration)}
          onDecrement={canShrink ? () => newDuration(sel.duration - BLOCK_HOURS) : undefined}
          onIncrement={canGrow ? () => newDuration(sel.duration + BLOCK_HOURS) : undefined}
        />
        <DateMover currentDate={sel.date} onMove={moveDate} />
        <label className="inline-flex items-center gap-1.5">
          <span className="text-ink-muted">Tech</span>
          <select
            value={sel.techId}
            onChange={(e) => {
              if (e.target.value !== sel.techId) {
                onReassignTech(sel.order.spireId, e.target.value);
              }
            }}
            aria-label="Reassign technician"
            className="rounded-md border bg-surface px-1.5 py-1 text-xs text-ink"
            style={{ borderColor: 'var(--color-border-tertiary)' }}
          >
            {/* A deactivated lead isn't in the roster; without its own option
               the select would silently display the first active tech. */}
            {!techs.some((t) => t.id === sel.techId) && (
              <option value={sel.techId}>{techName} (inactive)</option>
            )}
            {techs.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
        </label>
        <span className="text-[10px] text-ink-subtle">…or drag the card on the board.</span>
      </div>

      <OfficePhaseControls order={sel.order} />

      <HelpersPicker
        salesTypes={salesTypes}
        leadTechId={sel.techId}
        helperIds={readHelpers(sel.order.udf ?? {})}
        techs={techs}
        onChange={(next) => onSetHelpers(sel.order.spireId, next)}
      />

      <JobVisits
        spireId={sel.order.spireId}
        techs={techs}
        first={{ date: sel.date, startHour: sel.startHour, duration: sel.duration, techId: sel.techId }}
        onGoToDate={setSelectedDate}
      />
    </div>
  );
}

/**
 * Multi-select for helpers (techs assigned alongside the lead). Shown as
 * a chip row of currently-selected helpers + a popover to add more from
 * the active tech roster. Lead is excluded from the candidate list — they
 * can't also be a helper on their own job. Changes write FOP_HELPERS as
 * a single Spire UDF PUT on each toggle.
 */
function HelpersPicker({
  salesTypes,
  leadTechId,
  helperIds,
  techs,
  onChange,
}: {
  salesTypes: ReadonlyArray<SalesType>;
  leadTechId: string;
  helperIds: ReadonlyArray<string>;
  techs: ReadonlyArray<Technician>;
  onChange: (next: string[]) => void;
}) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const helperSet = new Set(helperIds);
  const candidates = techs.filter((t) => t.id !== leadTechId && !helperSet.has(t.id));
  const removeHelper = (id: string) =>
    onChange(helperIds.filter((h) => h !== id));
  const addHelper = (id: string) => {
    onChange([...helperIds, id]);
    setPickerOpen(false);
  };

  return (
    <div className="flex items-center gap-2 flex-wrap text-xs">
      <span className="text-[10px] uppercase tracking-wider text-ink-subtle inline-flex items-center gap-1">
        <Users className="h-3 w-3" aria-hidden />
        Helpers
      </span>
      {helperIds.length === 0 && (
        <span className="text-[11px] text-ink-subtle italic">None</span>
      )}
      {helperIds.map((id) => {
        const tech = techs.find((t) => t.id === id);
        return (
          <span
            key={id}
            className="inline-flex items-center gap-1 rounded-full border bg-surface px-2 py-0.5 text-[11px]"
            style={{ borderColor: 'var(--color-border-tertiary)' }}
          >
            <span className="text-ink">{tech?.name ?? id}</span>
            <button
              type="button"
              onClick={() => removeHelper(id)}
              aria-label={`Remove ${tech?.name ?? id}`}
              className="inline-flex h-4 w-4 items-center justify-center rounded-full text-ink-subtle hover:bg-surface-muted hover:text-red-700"
            >
              <X className="h-2.5 w-2.5" aria-hidden />
            </button>
          </span>
        );
      })}
      <div className="relative">
        <button
          type="button"
          onClick={() => setPickerOpen((v) => !v)}
          disabled={candidates.length === 0}
          aria-expanded={pickerOpen}
          className="inline-flex items-center gap-1 rounded-full border bg-surface px-2 py-0.5 text-[11px] text-ink-muted hover:bg-surface-muted hover:text-ink disabled:opacity-40 disabled:cursor-not-allowed"
          style={{ borderColor: 'var(--color-border-tertiary)' }}
          title={candidates.length === 0 ? 'No other active techs' : 'Add a helper'}
        >
          <UserPlus className="h-3 w-3" aria-hidden />
          Add helper
        </button>
        {pickerOpen && (
          <div
            className="absolute z-10 mt-1 left-0 min-w-[200px] rounded-md border bg-surface shadow-lg py-1"
            style={{ borderColor: 'var(--color-border-secondary)' }}
            onMouseLeave={() => setPickerOpen(false)}
          >
            {candidates.map((tech) => (
              <button
                key={tech.id}
                type="button"
                onClick={() => addHelper(tech.id)}
                className="w-full text-left px-3 py-1.5 text-xs text-ink hover:bg-surface-muted flex items-center justify-between gap-2"
              >
                <span>{tech.name}</span>
                <span className="text-[10px] text-ink-muted">
                  {tech.trades.map((t) => tradeLabel(salesTypes, t)).join(' · ')}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Cross-day reschedule control. -1d / +1d nudges + a native date input for
 * larger jumps. Used in DetailPanel — the per-card date stepper avoids the
 * unschedule-then-redrag-on-a-different-day dance that broke completed jobs
 * in particular (the completed flag was only cleared on assign, not on a
 * second drop after unschedule).
 */
function DateMover({
  currentDate,
  onMove,
}: {
  currentDate: string;
  onMove: (next: string) => void;
}) {
  const today = todayLocalIso();
  return (
    <div
      className="inline-flex items-stretch rounded-md border bg-surface overflow-hidden"
      style={{ borderColor: 'var(--color-border-tertiary)' }}
    >
      <span className="px-2 py-1 text-[10px] uppercase tracking-wider text-ink-subtle self-center inline-flex items-center gap-1">
        <Calendar className="h-3 w-3" aria-hidden />
        Date
      </span>
      <button
        type="button"
        onClick={() => onMove(shiftIsoDate(currentDate, -1))}
        aria-label="Move to previous day"
        className="inline-flex h-7 w-7 items-center justify-center border-l text-ink-muted hover:bg-surface-muted"
        style={{ borderColor: 'var(--color-border-tertiary)' }}
      >
        <Minus className="h-3 w-3" aria-hidden />
      </button>
      {/* Native date input — clicking it pops the OS date picker on every
         platform. We display the ISO value via toLocaleDateString below
         the steppers since browsers render the input value inconsistently
         in narrow widths. */}
      <input
        type="date"
        value={currentDate}
        onChange={(e) => {
          const next = e.target.value;
          if (next && next !== currentDate) onMove(next);
        }}
        className="px-2 py-1 text-xs text-ink tabular-nums border-l border-r bg-surface focus:outline-none focus:ring-2 focus:ring-inset focus:ring-ink-accent/30"
        style={{ borderColor: 'var(--color-border-tertiary)', minWidth: '128px' }}
        aria-label="Reschedule to date"
      />
      <button
        type="button"
        onClick={() => onMove(shiftIsoDate(currentDate, 1))}
        aria-label="Move to next day"
        className="inline-flex h-7 w-7 items-center justify-center text-ink-muted hover:bg-surface-muted"
      >
        <Plus className="h-3 w-3" aria-hidden />
      </button>
      {currentDate !== today && (
        <button
          type="button"
          onClick={() => onMove(today)}
          className="inline-flex items-center px-2 text-[10px] font-medium border-l text-ink-muted hover:bg-surface-muted hover:text-ink"
          style={{ borderColor: 'var(--color-border-tertiary)' }}
        >
          Today
        </button>
      )}
    </div>
  );
}

function Stepper({
  label,
  value,
  onDecrement,
  onIncrement,
}: {
  label: string;
  value: string;
  onDecrement?: () => void;
  onIncrement?: () => void;
}) {
  return (
    <div
      className="inline-flex items-stretch rounded-md border bg-surface overflow-hidden"
      style={{ borderColor: 'var(--color-border-tertiary)' }}
    >
      <span className="px-2 py-1 text-[10px] uppercase tracking-wider text-ink-subtle self-center">
        {label}
      </span>
      <button
        type="button"
        onClick={onDecrement}
        disabled={!onDecrement}
        aria-label={`Decrease ${label.toLowerCase()}`}
        className="inline-flex h-7 w-7 items-center justify-center border-l text-ink-muted hover:bg-surface-muted disabled:opacity-30 disabled:cursor-not-allowed"
        style={{ borderColor: 'var(--color-border-tertiary)' }}
      >
        <Minus className="h-3 w-3" aria-hidden />
      </button>
      <span className="px-2 py-1 text-xs font-medium text-ink tabular-nums min-w-[44px] text-center border-l border-r self-center" style={{ borderColor: 'var(--color-border-tertiary)' }}>
        {value}
      </span>
      <button
        type="button"
        onClick={onIncrement}
        disabled={!onIncrement}
        aria-label={`Increase ${label.toLowerCase()}`}
        className="inline-flex h-7 w-7 items-center justify-center text-ink-muted hover:bg-surface-muted disabled:opacity-30 disabled:cursor-not-allowed"
      >
        <Plus className="h-3 w-3" aria-hidden />
      </button>
    </div>
  );
}

function detailDate(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(y!, (m ?? 1) - 1, d ?? 1);
  return dt.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
}

const OFFICE_PHASES: ReadonlyArray<FieldPhase> = [
  'scheduled',
  'traveling',
  'on_site',
  'work_in_progress',
  'paused',
  'work_completed',
  'signed_off',
];

/**
 * Office-side phase / complete (Option B, 2026-09-23): sets the ORDER's
 * phase and, for Signed off, the completion stamp — through the API, which
 * never touches a tech's time entries. If a tech is on the job the API
 * says so and we show it; their own clock-out later corrects the stamp.
 */
function OfficePhaseControls({ order }: { order: SalesOrderSummary }) {
  const queryClient = useQueryClient();
  const pushActivity = useDispatchBoard((s) => s.pushActivity);
  const current = readOfficePhase(order.udf?.[FOP_UDF.phase]);
  const [notice, setNotice] = useState<string | null>(null);

  const set = useMutation<OfficePhaseResult, ApiError, FieldPhase>({
    mutationFn: (phase) => api.field.officeSetPhase(order.spireId, phase),
    onSuccess: (result, phase) => {
      void queryClient.invalidateQueries({ queryKey: ['operations', 'list'] });
      void queryClient.invalidateQueries({ queryKey: ['sales-order', order.spireId] });
      const what = phase === 'signed_off' ? 'Marked complete' : `Set to ${phaseLabel(phase)}`;
      pushActivity({ tone: phase === 'signed_off' ? 'success' : 'info', text: `${what}: ${result.orderNo} (from the office)` });
      if (result.onJob) {
        const since = new Date(result.onJob.since).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
        setNotice(
          `${result.onJob.techName ?? 'A tech'} is on this job (since ${since}). Their clock is untouched — when they clock out, their time sets the final phase and completion time.`,
        );
      } else {
        setNotice(null);
      }
    },
    onError: (err) => setNotice(`Couldn't update the phase: ${err.message}`),
  });

  return (
    <div className="rounded-md border px-3 py-2 space-y-2" style={{ borderColor: 'var(--color-border-tertiary)' }}>
      <div className="flex items-center gap-3 text-xs flex-wrap">
        <label className="inline-flex items-center gap-1.5">
          <span className="text-ink-muted">Phase</span>
          <select
            value={current ?? ''}
            disabled={set.isPending}
            onChange={(e) => {
              const next = e.target.value as FieldPhase | '';
              if (next && next !== current) set.mutate(next);
            }}
            className="h-8 rounded-md border bg-surface px-2 text-xs text-ink focus:outline-none focus:ring-2 focus:ring-brand-300"
            style={{ borderColor: 'var(--color-border-secondary)' }}
            aria-label="Job phase"
          >
            {current === null && <option value="">— not set —</option>}
            {OFFICE_PHASES.map((p) => (
              <option key={p} value={p}>
                {phaseLabel(p)}
              </option>
            ))}
          </select>
        </label>
        {current !== 'signed_off' && (
          <button
            type="button"
            disabled={set.isPending}
            onClick={() => set.mutate('signed_off')}
            className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium hover:bg-surface"
            style={{ backgroundColor: 'var(--feedback-success-bg)', color: 'var(--feedback-success-fg)' }}
          >
            <Check className="h-3.5 w-3.5" aria-hidden />
            {set.isPending ? 'Saving…' : 'Mark complete'}
          </button>
        )}
        <span className="text-[10px] text-ink-subtle">Sets the order's status; never stops a tech's clock.</span>
      </div>
      {notice && (
        <div
          className="rounded px-2 py-1.5 text-[11px]"
          style={{ backgroundColor: 'var(--feedback-warning-bg)', color: 'var(--feedback-warning-fg)' }}
        >
          {notice}
        </div>
      )}
    </div>
  );
}

function readOfficePhase(value: string | undefined): FieldPhase | null {
  return value && (OFFICE_PHASES as ReadonlyArray<string>).includes(value) ? (value as FieldPhase) : null;
}
